package com.htmlagent.site.state

import com.htmlagent.site.theme.Layout
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.w3c.dom.get
import org.w3c.dom.set

/**
 * How the person has the screen laid out.
 *
 * It is a preference of theirs and not session state: it survives a reload and lives in
 * `localStorage`, not on the server. Reads go through `runCatching` because `localStorage`
 * throws in private mode or with cookies blocked.
 */
object LayoutPrefs {

    private const val CHAT_WIDTH = "html-agent.chat-width"

    /**
     * Initial split as a share of the width, not in pixels: a fixed value works on the monitor
     * where it was chosen and badly everywhere else. As soon as the person drags, it is stored
     * in pixels because by then it is their own decision.
     */
    private const val DEFAULT_SHARE = 0.22

    /**
     * The stored width, clamped.
     *
     * The clamp used to apply only to the computed default, so a value already in storage came
     * back exactly as written - which is a width chosen on another screen, or from a build whose
     * bounds were different. Reading it back is the moment to check it against this window,
     * because it is the moment it stops being a number and becomes a layout.
     */
    fun chatWidth(): Int {
        val stored = runCatching { localStorage[CHAT_WIDTH]?.toIntOrNull() }.getOrNull()
        return clamp(stored ?: (window.innerWidth * DEFAULT_SHARE).toInt())
    }

    fun rememberChatWidth(px: Int) {
        runCatching { localStorage[CHAT_WIDTH] = clamp(px).toString() }
    }

    /** The widest the chat may get, which depends on the window and so is not a constant. */
    fun maxChatWidth(): Int =
        (window.innerWidth * Layout.ChatMaxShare).toInt().coerceAtLeast(Layout.ChatMinWidth + 1)

    /** Keeps a width readable at one end and leaves room for the page at the other. */
    fun clamp(px: Int): Int = px.coerceIn(Layout.ChatMinWidth, maxChatWidth())
}
